/**
 * LicenseModal — Reads content from content.json.
 * Zero hardcoded prose. GPL-licensed shell.
 */

import { useEffect, useRef } from 'react';
import content from '../../content.json';
import type { SiteContent } from '../types';

const data = (content as unknown as SiteContent).license;

interface LicenseModalProps {
  onClose: () => void;
}

const ICONS: Record<string, React.ReactNode> = {
  check: (
    <svg className="w-[18px] h-[18px] text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
  ),
  warning: (
    <svg className="w-[18px] h-[18px] text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
  ),
  shield: (
    <svg className="w-[18px] h-[18px] text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
  ),
};

export default function LicenseModal({ onClose }: LicenseModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-8 bg-slate-950/90 backdrop-blur-md" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="license-title">
      <div className="bg-slate-900 rounded-[2.5rem] w-full max-w-3xl max-h-[90vh] shadow-2xl overflow-y-auto custom-scrollbar border border-slate-800" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 px-8 py-6 border-b border-slate-800 bg-slate-900/95 backdrop-blur-sm flex items-center justify-between z-10">
          <div className="flex items-center gap-4 text-emerald-500">
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" /></svg>
            <h3 id="license-title" className="text-xl font-bold uppercase tracking-tight text-white">{data.title}</h3>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} className="text-slate-500 hover:text-white p-2 rounded-full transition-all" aria-label="Close">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        {/* Body */}
        <div className="p-8 md:p-12 space-y-12">
          {/* Summary */}
          <div className="p-8 bg-slate-800/50 rounded-[2rem] border border-slate-700/50">
            <p className="text-[10px] font-black text-slate-500 mb-3 uppercase tracking-widest">Core Summary</p>
            <p className="text-sm text-slate-300 font-bold leading-relaxed">{data.summary}</p>
          </div>

          {/* Sections grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {data.sections.map((section, i) => (
              <section key={i} className={`space-y-4 ${section.fullWidth ? 'md:col-span-2' : ''}`}>
                <h4 className="text-base font-bold text-white flex items-center gap-2">
                  {ICONS[section.icon] || ICONS.check}
                  {section.title}
                </h4>
                <ul className="text-[13px] text-slate-400 space-y-2 font-medium">
                  {section.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          {/* Disclaimer */}
          <div className="p-8 bg-emerald-500/5 rounded-[2rem] border border-emerald-500/10">
            <p className="text-xs font-bold text-slate-500 leading-relaxed">
              {data.disclaimer}
              <br /><br />
              {data.copyright.replace('Patrick T. Lafferty', '')}{' '}
              <a href={data.copyrightUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:underline">
                Patrick T. Lafferty
              </a>
            </p>
          </div>

          <div className="flex justify-center pt-4">
            <button type="button" onClick={onClose} className="px-10 py-4 bg-emerald-600 text-white rounded-2xl font-bold shadow-xl shadow-emerald-600/20 hover:bg-emerald-500 transition-all active:scale-95">
              {data.closeLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
